import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';

const NavigationHeader = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('/');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const location = useLocation();

  const navigationItems = [
    { 
      label: 'Home', 
      path: '/', 
      icon: 'Home',
      tooltip: 'Professional identity and value proposition'
    },
    { 
      label: 'About', 
      path: '/about', 
      icon: 'User',
      tooltip: 'Professional background and experience'
    },
    { 
      label: 'Skills', 
      path: '/skills', 
      icon: 'Code',
      tooltip: 'Technical capabilities and expertise'
    },
    { 
      label: 'Projects', 
      path: '/projects', 
      icon: 'FolderOpen',
      tooltip: 'Portfolio and case studies'
    },
    { 
      label: 'Experience', 
      path: '/experience', 
      icon: 'Briefcase',
      tooltip: 'Career progression and achievements'
    },
    { 
      label: 'Blog', 
      path: '/blog', 
      icon: 'FileText',
      tooltip: 'Insights on web development and AI'
    },
    { 
      label: 'Contact', 
      path: '/contact', 
      icon: 'Mail',
      tooltip: 'Get in touch and download resume'
    }
  ];

  useEffect(() => {
    const currentPath = location.pathname;
    const currentItem = navigationItems.find(item => item.path === currentPath);
    if (currentItem) {
      setActiveSection(currentItem.path);
    } else if (currentPath === '/') {
      setActiveSection('/');
    }
  }, [location.pathname]);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const shouldUseDark = savedTheme === 'dark' || (!savedTheme && prefersDark);
    
    setIsDarkMode(shouldUseDark);
    document.documentElement.classList.toggle('dark', shouldUseDark);
  }, []);

  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    document.documentElement.classList.toggle('dark', newTheme);
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    document.body.style.overflow = !isMobileMenuOpen ? 'hidden' : 'auto';
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    document.body.style.overflow = 'auto';
  };

  const handleNavClick = (path) => {
    setActiveSection(path);
    closeMobileMenu();
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-navigation bg-background/95 backdrop-blur-sm border-b border-border">
        <nav className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center space-x-2 hover:opacity-80 transition-opacity duration-200"
              onClick={() => handleNavClick('/')}
            >
              <div className="w-8 h-8 bg-primary rounded-md flex items-center justify-center">
                <Icon name="Code" size={20} color="white" />
              </div>
              <span className="text-xl font-bold text-text-primary">Dishant Madariya</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
                  onClick={() => handleNavClick(item.path)}
                  title={item.tooltip}
                >
                  <Icon name={item.icon} size={16} className="inline mr-2" />
                  {item.label}
                </Link>
              ))}
            </div>

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center space-x-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="theme-toggle"
                title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                <Icon name={isDarkMode ? 'Sun' : 'Moon'} size={20} />
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center space-x-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="theme-toggle"
                title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                <Icon name={isDarkMode ? 'Sun' : 'Moon'} size={20} />
              </Button>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleMobileMenu}
                className="hamburger"
                aria-label="Toggle mobile menu"
              >
                <div className={`hamburger ${isMobileMenuOpen ? 'open' : ''}`}>
                  <span className="hamburger-line"></span>
                  <span className="hamburger-line"></span>
                  <span className="hamburger-line"></span>
                </div>
              </Button>
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-mobile-menu md:hidden">
          <div 
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={closeMobileMenu}
          ></div>
          <div className="relative bg-surface shadow-lg animate-slide-down">
            <div className="px-6 py-4 border-b border-border">
              <div className="flex items-center justify-between">
                <Link 
                  to="/" 
                  className="flex items-center space-x-2"
                  onClick={closeMobileMenu}
                >
                  <div className="w-8 h-8 bg-primary rounded-md flex items-center justify-center">
                    <Icon name="Code" size={20} color="white" />
                  </div>
                  <span className="text-xl font-bold text-text-primary">Dishant Madariya</span>
                </Link>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={closeMobileMenu}
                  aria-label="Close mobile menu"
                >
                  <Icon name="X" size={24} />
                </Button>
              </div>
            </div>
            <div className="py-4">
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`mobile-nav-link ${location.pathname === item.path ? 'active' : ''}`}
                  onClick={() => handleNavClick(item.path)}
                >
                  <Icon name={item.icon} size={20} className="inline mr-3" />
                  <span className="font-medium">{item.label}</span>
                  <p className="text-sm text-text-secondary mt-1 ml-8">{item.tooltip}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NavigationHeader;